import { DialogTitle } from "@radix-ui/react-dialog";
import { memo, useCallback, useMemo, useState } from "react";

import { Button } from "@/components/ui/button/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog/dialog";
import { InputNumberField } from "@/components/ui/form/fields/input-field/input-number";

import { useHandleFormState } from "./hooks";

type Props = {
	// 請求可能なキャンセル料金の上限（保持した本来のキャンセル料）
	// 呼び出し側（inner.tsx）が値の存在を確認してからモーダルを表示するため必須。
	willCancelFeeChargePrice: number;
	//
	onChargeCancelFee: (chargePrice: number) => Promise<void>;
};

/**
 * 与信失敗による強制キャンセル後、施設がゲストへキャンセル料を手動請求するモーダル。
 * 既存のキャンセルモーダルを踏襲し、文言と下限（1円以上）のみ変更している。
 */
export const ReservationDetailChargeCancelFeeModal = memo(({ willCancelFeeChargePrice, onChargeCancelFee }: Props) => {
	const {
		chargePrice,
		chargePriceErrorMessage,
		hasError,
		//
		handleChangeChargePrice,
	} = useHandleFormState(willCancelFeeChargePrice);

	const [canOpenModal, setCanOpenModal] = useState(false);
	/**
	 * モーダルを表示
	 */
	const handleClickOpenModal = useCallback(() => {
		setCanOpenModal(true);
	}, []);
	/**
	 * モーダルを非表示
	 */
	const handleClickCloseModal = useCallback(() => {
		setCanOpenModal(false);
	}, []);
	/**
	 * モーダルの表示・非表示を制御
	 */
	const onChangeCanOpenModal = useCallback((open: boolean) => {
		setCanOpenModal(open);
	}, []);
	/**
	 * モーダルの表示・非表示を制御
	 */
	const handleOpenChange = useCallback((open: boolean) => {
		setCanOpenModal(open);
	}, []);

	/**
	 * キャンセル料金を請求する
	 * 請求は1回限りで取り消せないため、実行前に金額の確認ダイアログを挟む
	 */
	const handleClickChargeCancelFee = useCallback(() => {
		if (hasError || chargePrice === undefined) return;

		const confirmText = `キャンセル料金として ${chargePrice.toLocaleString()} 円をゲストへ請求します。\nこの操作は取り消すことが出来ません。本当に請求しますか？\n（入力金額に誤りがないか必ずご確認ください。）`;
		if (!window.confirm(confirmText)) return;

		onChargeCancelFee(chargePrice).then(() => onChangeCanOpenModal(false));
	}, [chargePrice, hasError, onChangeCanOpenModal, onChargeCancelFee]);

	const willCancelFeeChargePriceText = useMemo(
		() => willCancelFeeChargePrice.toLocaleString(),
		[willCancelFeeChargePrice],
	);

	/**
	 * 請求可能額を超える、もしくはエラーがある場合は実行不可
	 */
	const disabled = useMemo(
		() => hasError || (chargePrice ?? 0) > willCancelFeeChargePrice,
		[chargePrice, hasError, willCancelFeeChargePrice],
	);

	/**
	 * モーダルを表示するボタン
	 */
	if (!canOpenModal) {
		return (
			<Button variant="outline" type="button" size="sm" onClick={handleClickOpenModal}>
				キャンセル料を請求する
			</Button>
		);
	}

	return (
		<Dialog open={canOpenModal} onOpenChange={handleOpenChange}>
			<DialogContent className="">
				<DialogHeader className="border-b border-b-zinc-200 pb-4">
					<DialogTitle>キャンセル料の請求</DialogTitle>
				</DialogHeader>

				<div className="space-y-4 p-2">
					{/* 説明 */}
					<p className="text-sm text-zinc-600">
						決済エラーにより自動キャンセルされた予約に対して、キャンセル料金を請求できます。
						<br />
						請求するとゲストへメールが送信され、ゲストの予約詳細画面に支払いフォームが表示されます。
					</p>

					<div>
						<p className="text-sm">
							請求できる最大のキャンセル料金は
							<span className="text-2xl underline">{willCancelFeeChargePriceText}</span>
							円です。
						</p>
					</div>

					<div>
						<div className="flex items-end gap-x-3">
							<div className="min-w-60 max-w-60">
								<InputNumberField
									label="請求するキャンセル料金"
									value={chargePrice}
									handleInput={handleChangeChargePrice}
									errorMessage={chargePriceErrorMessage}
								/>
							</div>

							<span className="text-sm font-semibold">円</span>
						</div>
					</div>
				</div>

				<div className="flex items-center justify-end gap-x-3 border-t border-t-zinc-200 px-6 pt-4">
					<Button variant="outline" type="button" onClick={handleClickCloseModal}>
						戻る
					</Button>

					<Button type="button" disabled={disabled} onClick={handleClickChargeCancelFee}>
						請求する
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
});
